import React, { useState, useEffect, useRef } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Button, TextField, makeStyles, Popover } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import axios from 'axios';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import numeral from 'numeral';
import { mapColor } from 'src/helper';
import { SrchSub } from 'src/components';
import { isEmpty } from 'lodash';
import Link from 'src/components/Field/Link';
import DateField from 'src/components/Field/DateField';
import { Field } from 'src/components';
const useStyles = makeStyles(theme => ({
  editButton: {
    display: 'none',
    position: 'absolute',
    right: 0,
    top: 0,

    [theme.breakpoints.down('md')]: {
      display: 'block'
    },
    [theme.breakpoints.down('xs')]: {
      display: 'block'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    }
  },
  parentItem: {
    width: '100%',
    position: 'relative',
    '&:hover': {
      '& $editButton': {
        display: 'block'
      }
    }
  },
  caption: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  captionBold: {
    fontWeight: 900
  },

  pointer: {
    cursor: 'pointer'
  }
}));

const saveDetails = async ({ base_url, uuid, userToken, ...rest }) => {
  try {
    const { data } = await axios({
      method: 'PUT',
      url: `${base_url}/trans/jcd/${uuid}/save `,
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': userToken
      },
      data: rest
    });

    return {
      success: true,
      data
    };
  } catch (error) {
    let message = '';

    try {
      if (error?.response?.data?.item?.validation_errors) {
        const errors = Object.entries(
          error.response.data.item.validation_errors
        ).map(([key, value]) => value);

        message = `Error : ${errors.join(', ')} `;
        return {
          success: false,
          errorMessage: message
        };
      }

      throw error;
    } catch (error) {
      message = `Error : ${
        error?.response?.data ? error.response.data.msg : 'Something went wrong'
      }`;
    }

    return {
      success: false,
      errorMessage: message
    };
  }
};

const InputComponent = ({
  value,
  settings,
  handleChange,
  type,
  error,
  errorMessage
}) => {
  let val;
  if (type === 'date')
    val = value === '' ? '' : moment(value).format('YYYY-MM-DD');
  else val = value;

  if (settings?.ui_component !== 'link' && !['date', 'json'].includes(type)) {
    const { caption = '', ...otherSettings } = settings;
    return (
      <Field
        data={{ value: value, type: type, caption: '', ...otherSettings }}
        onChangeField={e => handleChange(e.target.value)}
      />
    );
  }

  if (settings?.ui_component === 'select') {
    return (
      <TextField
        select
        fullWidth
        error={error}
        helperText={errorMessage}
        value={value}
        variant="outlined"
        onChange={e => handleChange(e.target.value)}
        size="small"
      >
        <MenuItem value="">Please select</MenuItem>
        {settings.options.map(data => (
          <MenuItem key={data.value} value={data.value}>
            {data.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (type === 'int') {
    return (
      <TextField
        type="number"
        value={value}
        onChange={e => handleChange(e.target.value)}
        variant="outlined"
        error={error}
        helperText={errorMessage}
        fullWidth
        size="small"
      />
    );
  }

  if (type === 'date') {
    return (
      <DateField
        data={{
          value: val,
          error,
          errorMessage,
          rest: {
            size: 'small',
            default: true
          }
        }}
        onChangeField={e => handleChange(e.target.value)}
      />
    );
  }

  if (type === 'json' && settings?.ui_component === 'sub_acc') {
    return (
      <SrchSub
        size="small"
        ixSubType={settings?.ixSubType}
        selectedSub={value?.sSub ?? ''}
        error={error}
        errorMessage={errorMessage}
        handleSubSelectRoot={data => {
          handleChange({
            id: data.ixSub,
            sSub: data.sSub
          });
        }}
      />
    );
  }

  if (type === 'json' && settings?.ui_component === 'link') {
    return (
      <Link
        data={{ value }}
        onChangeField={e => handleChange(e.target.value)}
      />
    );
  }

  return (
    <TextField
      type={type}
      value={val}
      onChange={e => handleChange(e.target.value)}
      variant="outlined"
      error={error}
      helperText={errorMessage}
      fullWidth
      size="small"
    />
  );
};

const listValue = (value, options) =>
  options.find(item => item.value == value)?.label;

const chipColor = (value, options) =>
  options.find(item => item.value === value)?.color;

const SelectComponent = ({ settings, value }) => {
  const listValueColor = settings?.options
    ? mapColor(chipColor(value, settings.options))
    : '';

  return (
    <Chip
      style={{
        backgroundColor: listValueColor.background,
        color: listValueColor.color
      }}
      size="small"
      label={settings?.options ? listValue(value, settings.options) : ''}
    />
  );
};

const getDataFormatToPresent = (value, settings) => {
  if (typeof value === 'undefined') return '';

  if (settings?.ui_component === 'number')
    return numeral(value).format('0,0.00');

  if (settings?.ui_component === 'sub_acc') return value?.sSub ?? '';

  if (settings?.ui_component === 'product') {
    if (settings?.qty?.show) {
      return `${value?.sProd ?? ''} (${numeral(value?.qty ?? 0).format(
        '0,0.0'
      )})`;
    } else return value?.sProd ?? '';
  }

  if (settings?.ui_component === 'link')
    return <Link data={{ value }} editMode={false} />;

  if (settings?.ui_component === 'select')
    return <SelectComponent settings={settings} value={value} />;

  if (settings?.type === 'date') return moment(value).format('MM/DD/YYYY');

  // Handle object values
  if (
    typeof value === 'object' &&
    value !== null &&
    !React.isValidElement(value)
  ) {
    // If it has amount, show formatted amount
    if (value.amount !== undefined) {
      return numeral(value.amount).format('0,0.00');
    }
    if (value.sProd !== undefined) {
      return value.sProd;
    }
    if (Array.isArray(value)) {
      return `${value.length} item${value.length !== 1 ? 's' : ''}`;
    }
    if (value.name || value.label || value.title || value.description) {
      return value.name || value.label || value.title || value.description;
    }
    const keys = Object.keys(value);
    if (keys.length > 0) {
      const firstValue = value[keys[0]];
      if (typeof firstValue === 'string') {
        return firstValue;
      }
      if (typeof firstValue === 'object' && firstValue?.sProd) {
        return firstValue.sProd;
      }
    }
    return `[Object ${keys.length} properties]`;
  }

  return value;
};

const OtherDetailsItem = ({
  jid,
  uuid,
  ixTL,
  value,
  keyItem,
  settings,
  base_url,
  userToken,
  handleSetEditMode,
  handleSetDetailsChange
}) => {
  // const classes = useStyles();

  const classes = useStyles();
  const ref = useRef();

  const oldValue = value[keyItem];
  const [anchorEl, setAnchorEl] = useState(null);
  const [itemValue, setItemValue] = useState(oldValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const open = Boolean(anchorEl);
  const target = settings?.target ?? 'kvs';
  const id = open ? 'simple-popover' : undefined;

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleItemChange = val => {
    if (settings.type === 'date') {
      setItemValue(moment(`${val} ${new Date().toTimeString()}`).format());
      return;
    }

    setItemValue(val);
  };

  const handleClose = () => {
    setItemValue(oldValue);
    setError(false);
    setErrorMessage('');
    handleSetEditMode(keyItem);
  };

  const validate = () => {
    const isRequired =
      settings?.required || settings?.isRequired || settings?.require;

    if (!isRequired) {
      return {
        success: true,
        msg: `Success`
      };
    }

    if (settings?.type === 'json' && settings?.ui_component === 'sub_acc') {
      const empty =
        !Boolean(itemValue) ||
        isEmpty(itemValue) ||
        itemValue?.id === '' ||
        itemValue?.id === 0;

      if (empty) {
        return {
          success: false,
          msg: `${settings.caption} is required.`
        };
      }
    }

    if (!['date', 'json'].includes(settings.type)) {
      const empty = itemValue === '' || itemValue === null;
      if (empty) {
        return {
          success: false,
          msg: `${settings.caption} is required.`
        };
      }
    }

    return {
      success: true,
      msg: `Success`
    };
  };

  const handleSaveTransaction = async () => {
    setLoading(true);

    const { msg, success } = validate();

    if (!success) {
      setError(true);
      setErrorMessage(msg);
      setLoading(false);
      return;
    }

    const data = {
      jid,
      base_url,
      userToken,
      uuid,
      ixTL
    };

    if (target === 'jv') data[keyItem] = itemValue;
    else {
      data.kvs = {};
      data.kvs[keyItem] = itemValue;
    }

    const result = await saveDetails(data);

    if (result.success) {
      setError(false);
      setErrorMessage('');
      setLoading(false);
      handleSetEditMode(keyItem);
      handleSetDetailsChange({ key: keyItem, value: itemValue });
      return;
    }

    setError(true);
    setErrorMessage(result.errorMessage);
    setLoading(false);
  };

  //if jv has changes it will re-render the component
  useEffect(() => {
    setItemValue(value[keyItem]);
  }, [oldValue]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      height="100%"
      className={classes.parentItem}
    >
      <Box width="100%">
        <Typography
          variant="caption"
          display="block"
          className={classes.caption}
        >
          {settings.caption}
        </Typography>

        <Box>
          <Typography
            variant="subtitle2"
            gutterBottom
            style={{ marginTop: '.5em' }}
          >
            {settings.isEditMode
              ? InputComponent({
                  value: itemValue,
                  handleChange: handleItemChange,
                  settings: settings,
                  type: settings.type,
                  error,
                  errorMessage
                })
              : getDataFormatToPresent(itemValue, settings)}
          </Typography>

          {settings?.isEditable && settings.isEditMode && (
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Button size="small" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                size="small"
                color="primary"
                onClick={handleSaveTransaction}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress
                      color="inherit"
                      size={15}
                      style={{ marginRight: '.5em' }}
                    />
                  ) : null
                }
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
        {Boolean(settings?.notes) && (
          <>
            <Typography
              className={`${classes.caption} ${classes.pointer}`}
              variant="caption"
              onClick={handlePopoverOpen}
            >
              {settings?.notes ?? ''}
            </Typography>

            <Popover
              id="mouse-over-popover"
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              onClose={handlePopoverClose}
            >
              <Box width={300} p={2}>
                <Typography
                  variant="caption"
                  className={classes.captionBold}
                  display="block"
                >
                  WF Notes
                </Typography>
                <Box mt={1}>
                  <Typography variant="caption">
                    {settings?.notes ?? ''}
                  </Typography>
                </Box>
              </Box>
            </Popover>
          </>
        )}
      </Box>
      {settings?.isEditable && !settings.isEditMode && (
        <Box alignSelf="center" className={classes.editButton} ref={ref}>
          <Tooltip disableFocusListener title="Edit">
            <IconButton onClick={handleClose}>
              <EditIcon style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {settings?.callback && settings?.isEditable && (
        <Box alignSelf="center" className={classes.editButton} ref={ref}>
          <Tooltip disableFocusListener title="Edit">
            <IconButton onClick={settings.callback}>
              <EditIcon style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default OtherDetailsItem;
